import { useMemo } from "react";
import { TFunction } from "react-i18next";

import { QueryGroupCombinator, QueryGroupOperator } from "./types";

export const useWorflowOperators = (
  t: TFunction<"queryBuilder">
): {
  combinators: QueryGroupCombinator[];
  operators: Record<string, QueryGroupOperator[]>;
} => {
  const combinators = useMemo(
    () => [
      { operand: "and", label: t("combinators.and") },
      { operand: "or", label: t("combinators.or") },
    ],
    [t]
  );

  const operators = useMemo(() => {
    return {
      numeric: [
        {
          operator: "greaterThan",
          label: t("operators.numeric.greaterThan"),
          combinators: ["and"],
        },
        {
          operator: "lessThan",
          label: t("operators.numeric.lessThan"),
          combinators: ["and"],
        },
        {
          operator: "equals",
          label: t("operators.numeric.equalsTo"),
          combinators: ["and", "or"],
        },
        {
          operator: "greaterThanEq",
          label: t("operators.numeric.greaterThanEq"),
          combinators: ["and"],
        },
        {
          operator: "lessThanEq",
          label: t("operators.numeric.lessThanEq"),
          combinators: ["and"],
        },
        {
          operator: "notEqual",
          label: t("operators.numeric.notEqual"),
          combinators: ["and"],
        },
        {
          operator: "range",
          label: t("operators.numeric.range"),
          combinators: ["and"],
        },
      ],
      text: [
        {
          operator: "equals",
          label: t("operators.text.equals"),
          combinators: ["and", "or"],
        },
        {
          operator: "equalsIgnoreCase",
          label: t("operators.text.equalsIgnoreCase"),
          combinators: ["and", "or"],
        },
        {
          operator: "contains",
          label: t("operators.text.Contains"),
          combinators: ["and", "or"],
        },
        {
          operator: "StartsWith",
          label: t("operators.text.StartsWith"),
          combinators: ["and", "or"],
        },
        {
          operator: "EndsWith",
          label: t("operators.text.EndsWith"),
          combinators: ["and", "or"],
        },
        {
          operator: "is_not_empty",
          label: t("operators.text.IsNotEmpty"),
          combinators: ["and"],
        },
        {
          operator: "IsNot",
          label: t("operators.text.IsNot"),
          combinators: ["and"],
        },
        {
          operator: "Empty",
          label: t("operators.text.Empty"),
          combinators: ["and"],
        },
      ],
      textarea: [
        {
          operator: "equals",
          label: t("operators.textarea.equals"),
          combinators: ["and", "or"],
        },
        {
          operator: "equalsIgnoreCase",
          label: t("operators.textarea.equalsIgnoreCase"),
          combinators: ["and", "or"],
        },
        {
          operator: "contains",
          label: t("operators.textarea.Contains"),
          combinators: ["and", "or"],
        },
        {
          operator: "StartsWith",
          label: t("operators.textarea.StartsWith"),
          combinators: ["and", "or"],
        },
        {
          operator: "EndsWith",
          label: t("operators.textarea.EndsWith"),
          combinators: ["and", "or"],
        },
        {
          operator: "is_not_empty",
          label: t("operators.textarea.IsNotEmpty"),
          combinators: ["and"],
        },
        {
          operator: "IsNot",
          label: t("operators.textarea.IsNot"),
          combinators: ["and"],
        },
        {
          operator: "Empty",
          label: t("operators.textarea.Empty"),
          combinators: ["and"],
        },
      ],
      boolean: [
        {
          operator: "is",
          label: t("operators.boolean.equalsTo"),
          combinators: ["and"],
        },
      ],
      dateandtime: [
        {
          operator: "greaterThan",
          label: t("operators.dateandtime.greaterThan"),
          combinators: ["and"],
        },
        {
          operator: "lessThan",
          label: t("operators.dateandtime.lessThan"),
          combinators: ["and"],
        },
        {
          operator: "equals",
          label: t("operators.dateandtime.equalsTo"),
          combinators: ["and", "or"],
        },
        {
          operator: "greaterThanEq",
          label: t("operators.dateandtime.greaterThanEq"),
          combinators: ["and"],
        },
        {
          operator: "lessThanEq",
          label: t("operators.dateandtime.lessThanEq"),
          combinators: ["and"],
        },
        {
          operator: "notEqual",
          label: t("operators.dateandtime.notEqual"),
          combinators: ["and"],
        },
        {
          operator: "range",
          label: t("operators.dateandtime.range"),
          combinators: ["and"],
        },
      ],
      distance: [],
    };
  }, [t]);

  return { combinators, operators };
};
